import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface HeaderProps {
  isTransparent?: boolean;
}

export default function Header({ isTransparent = false }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    if (location.pathname === '/') {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        setIsMobileMenuOpen(false);
      }
    } else {
      // 他のページからはホームページに遷移してからスクロール
      window.location.href = `/#${id}`;
    }
  };

  const isHomePage = location.pathname === '/';
  const isCompanyPage = location.pathname === '/company';
  const isContactPage = location.pathname === '/contact';

  // 背景色の決定
  const getNavBg = () => {
    if (isTransparent && isHomePage) {
      return isScrolled ? 'bg-white shadow-md' : 'bg-transparent';
    }
    return isScrolled ? 'bg-white shadow-md' : 'bg-teal-600';
  };

  // テキスト色の決定
  const getTextColor = (isActive = false) => {
    if (isTransparent && isHomePage && !isScrolled) {
      return isActive ? 'text-teal-200' : 'text-white';
    }
    if (isScrolled) {
      return isActive ? 'text-teal-600' : 'text-gray-700';
    }
    return isActive ? 'text-teal-200' : 'text-white';
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${getNavBg()}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex-shrink-0">
            <Link to="/" className="flex flex-col">
              <h1 className={`text-2xl font-bold transition-colors ${isScrolled || !isTransparent || !isHomePage ? 'text-gray-900' : 'text-white'}`}>
                株式会社ArrayVenture
              </h1>
            </Link>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            {isHomePage ? (
              <button onClick={() => scrollToSection('home')} className={`text-sm font-medium transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${getTextColor()}`}>
                ホーム
              </button>
            ) : (
              <Link to="/" className={`text-sm font-medium transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${getTextColor()}`}>
                ホーム
              </Link>
            )}
            <Link to="/company" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${getTextColor(isCompanyPage)}`}>
              会社概要
            </Link>
            {isHomePage ? (
              <button onClick={() => scrollToSection('services')} className={`text-sm font-medium transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${getTextColor()}`}>
                事業内容
              </button>
            ) : (
              <Link to="/#services" className={`text-sm font-medium transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${getTextColor()}`}>
                事業内容
              </Link>
            )}
            {isHomePage ? (
              <button onClick={() => scrollToSection('projects')} className={`text-sm font-medium transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${getTextColor()}`}>
                実績・事例
              </button>
            ) : (
              <Link to="/#projects" className={`text-sm font-medium transition-colors hover:text-teal-500 whitespace-nowrap cursor-pointer ${getTextColor()}`}>
                実績・事例
              </Link>
            )}
            {isContactPage ? (
              <button onClick={() => {
                const element = document.getElementById('contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }} className="bg-teal-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                お問い合わせ
              </button>
            ) : (
              <Link to="/contact#contact" className="bg-teal-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                お問い合わせ
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer">
            <i className={`text-2xl ${isScrolled || !isTransparent || !isHomePage ? 'text-gray-900' : 'text-white'} ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-4 py-3 space-y-3">
            {isHomePage ? (
              <button onClick={() => scrollToSection('home')} className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-500 py-2 cursor-pointer">
                ホーム
              </button>
            ) : (
              <Link to="/" className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-500 py-2 cursor-pointer">
                ホーム
              </Link>
            )}
            <Link to="/company" className={`block w-full text-left text-sm font-medium py-2 cursor-pointer ${isCompanyPage ? 'text-teal-600' : 'text-gray-700 hover:text-teal-500'}`}>
              会社概要
            </Link>
            {isHomePage ? (
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-500 py-2 cursor-pointer">
                事業内容
              </button>
            ) : (
              <Link to="/#services" className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-500 py-2 cursor-pointer">
                事業内容
              </Link>
            )}
            {isHomePage ? (
              <button onClick={() => scrollToSection('projects')} className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-500 py-2 cursor-pointer">
                実績・事例
              </button>
            ) : (
              <Link to="/#projects" className="block w-full text-left text-sm font-medium text-gray-700 hover:text-teal-500 py-2 cursor-pointer">
                実績・事例
              </Link>
            )}
            {isContactPage ? (
              <button onClick={() => {
                const element = document.getElementById('contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                  setIsMobileMenuOpen(false);
                }
              }} className="block w-full text-left bg-teal-500 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                お問い合わせ
              </button>
            ) : (
              <Link to="/contact#contact" className="block w-full text-left bg-teal-500 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                お問い合わせ
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
